import React, { useState } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import AddClientLocation from './AddClientLocation';
import { clearCameraClientLoc } from './cameraApi';
import Toast from './Toast';

const InfoTile = ({ label, value, icon }) => (
  <div className="bg-gray-50 rounded-xl p-5 flex-1 min-w-0">
    <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
      {icon}
      {label}
    </div>
    <p className={`text-lg font-semibold truncate ${value ? 'text-gray-800' : 'text-gray-400 italic font-normal'}`}>
      {value || 'Non renseigné'}
    </p>
  </div>
);

const MotorInformation = ({ motor, user, setMotor }) => {
  const clientLoc = typeof motor.clientLoc === 'object' ? motor.clientLoc : null;
  const client = clientLoc?.client;
  const localisation = clientLoc?.localisation;

  const [showForm, setShowForm] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ type: '', message: '' });

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast({ type: '', message: '' }), 3000);
  };

  const handleFormSuccess = (updatedCamera) => {
    setMotor((prev) => ({ ...prev, clientLoc: updatedCamera.clientLoc }));
    setShowForm(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await clearCameraClientLoc(motor.id, user.token);
      setMotor((prev) => ({ ...prev, clientLoc: null }));
      setConfirmingDelete(false);
      showToast('success', 'Client et localisation supprimés.');
    } catch (error) {
      console.error('Erreur lors de la suppression du client/localisation:', error);
      showToast('error', 'Échec de la suppression.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-6 sm:p-8 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1.6.8L10 14.25l-4.4 2.55A1 1 0 014 16V4zm2 0v10.798l3.4-1.965a1 1 0 011 0L14 14.798V4H6z" clipRule="evenodd" />
          </svg>
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Informations du Compteur</h2>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <InfoTile label="Client" value={client} />
        <InfoTile label="Localisation" value={localisation} />
      </div>

      {showForm ? (
        <AddClientLocation
          motor={motor}
          token={user.token}
          initialClient={client || ''}
          initialLocalisation={localisation || ''}
          onSuccess={handleFormSuccess}
          onCancel={() => setShowForm(false)}
        />
      ) : client && localisation ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg px-4 py-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Client et localisation déjà définis
          </div>

          {confirmingDelete ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-red-50 rounded-lg px-4 py-3">
              <p className="text-sm text-red-700 font-medium">
                Supprimer le client et la localisation associés ?
              </p>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Suppression...' : 'Confirmer'}
                </button>
                <button
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                  className="bg-white border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-5 py-2.5 rounded-lg shadow-sm hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 font-medium text-sm"
              >
                <FaEdit className="text-blue-500" />
                Modifier
              </button>
              <button
                onClick={() => setConfirmingDelete(true)}
                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-5 py-2.5 rounded-lg shadow-sm hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-200 font-medium text-sm"
              >
                <FaTrash className="text-red-400" />
                Supprimer
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 font-medium"
        >
          Ajouter un client et une localisation
        </button>
      )}

      <Toast type={toast.type} message={toast.message} />
    </div>
  );
};

export default MotorInformation;